/**
 * Tiny typed surface for the JSON WebSocket envelope (Plan 25 Phase C).
 *
 * The wire types live in {@link ./generated/} (regenerated from
 * `crates/rhia-protocol` via `pnpm sync:protocol`). This module re-exports
 * them for ergonomics and adds the `WsClient` interface that both the
 * browser implementation (`apps/editor/src/lib/ws-client.ts`) and the Node
 * implementation (`apps/cloud-api/src/services/master-ws-client.ts`)
 * implement.
 *
 * Don't put any logic here that diverges from the Rust crate. Schema
 * changes belong upstream.
 */

import type { Command } from './generated/Command.js';
import type { WsRequest } from './generated/WsRequest.js';
import type { WsResponse } from './generated/WsResponse.js';
import type { WsTopic } from './generated/WsTopic.js';

export type { Command } from './generated/Command.js';
export type { CommandEnvelope } from './generated/CommandEnvelope.js';
export type { EventPayload } from './generated/EventPayload.js';
export type { ResultPayload } from './generated/ResultPayload.js';
export type { ResultStatus } from './generated/ResultStatus.js';
export type { WsRequest } from './generated/WsRequest.js';
export type { WsResponse } from './generated/WsResponse.js';
export type { WsTopic } from './generated/WsTopic.js';

/**
 * Options accepted by {@link WsClient.sendCommand}.
 */
export interface SendCommandOptions {
  /**
   * Time, in milliseconds, after which the inflight promise rejects with a
   * timeout error if no `result`/`error` frame has arrived. Defaults are
   * implementation-specific.
   */
  timeoutMs?: number;
}

/**
 * Subset of the master's WebSocket protocol exposed to callers — auth,
 * typed command/response correlation, and topic subscriptions.
 *
 * Both {@link createBrowserWsClient} (editor) and
 * {@link createMasterWsClient} (cloud-api) implement this interface.
 */
export interface WsClient {
  /**
   * Sends the auth handshake. Resolves once the master accepts the token,
   * rejects with an `Error` if the master replies with `kind:'error'`.
   */
  auth(token: string): Promise<void>;

  /**
   * Sends a typed {@link Command} and resolves with the matching `result`
   * (or rejects on `error` / timeout). The returned promise carries the
   * full {@link WsResponse} so callers can inspect `status`/`payload`.
   */
  sendCommand(command: Command, opts?: SendCommandOptions): Promise<WsResponse>;

  /**
   * Asks the master to start streaming events for the given topics.
   * Idempotent — subscribing twice is fine.
   */
  subscribe(topics: WsTopic[]): Promise<void>;

  /**
   * Cancels server-side streaming for the given topics.
   */
  unsubscribe(topics: WsTopic[]): Promise<void>;

  /**
   * Registers a handler for a topic. The handler is invoked with the full
   * {@link WsResponse} (`kind:'event'`) so callers can read both `topic`
   * and `payload`. Returns an unsubscribe function that removes only this
   * handler — it does NOT send `unsubscribe` to the server.
   */
  onEvent(topic: WsTopic, handler: (frame: WsResponse) => void): () => void;

  /**
   * Closes the underlying socket and rejects any inflight commands.
   */
  close(): void;
}

/**
 * Generates a process-unique request id for correlating
 * `WsRequest{kind:'command'}` with the matching `WsResponse{kind:'result'|'error'}`.
 *
 * Implementation: monotonic counter prefixed with a random 4-char suffix
 * so two clients in the same browser tab don't collide. The id is opaque
 * to the master; callers may pass any unique string they like — this is
 * just the default helper.
 */
let _idCounter = 0;
const _idPrefix = ((): string => {
  // Cheap random suffix to avoid clashes between two long-lived clients
  // sharing this module instance (e.g. cloud-api process with two
  // master sockets).
  const n = Math.floor(Math.random() * 0xffff);
  return n.toString(16).padStart(4, '0');
})();

export function nextRequestId(): string {
  _idCounter = (_idCounter + 1) >>> 0;
  return `${_idPrefix}-${_idCounter.toString(36)}`;
}

/**
 * Stable string key for a {@link WsTopic} so it can be used as a Map key.
 * `live_tag` and `sequence_run` carry an id; we hash that into the key.
 */
export function topicKey(topic: WsTopic): string {
  switch (topic.kind) {
    case 'live_tag':
      return `live_tag:${topic.tag_id}`;
    case 'sequence_run':
      return `sequence_run:${topic.run_id}`;
    default:
      return topic.kind;
  }
}

/**
 * Internal helper — strongly-typed builder for the `auth` request.
 * Exported so both implementations stay in lock-step.
 */
export function buildAuthRequest(token: string): WsRequest {
  return { kind: 'auth', token };
}

/**
 * Internal helper — strongly-typed builder for a `command` request.
 */
export function buildCommandRequest(id: string, command: Command): WsRequest {
  return { kind: 'command', id, command };
}

/**
 * Internal helper — strongly-typed builder for `subscribe`/`unsubscribe`.
 */
export function buildSubscribeRequest(topics: WsTopic[]): WsRequest {
  return { kind: 'subscribe', topics };
}

export function buildUnsubscribeRequest(topics: WsTopic[]): WsRequest {
  return { kind: 'unsubscribe', topics };
}
